import React, { useEffect, useRef, useState } from "react";
import Loading from "../common/Loading";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { MESSAGES, trackApi, trackErrorMessage } from "./trackApi";
import { API_BASE_URL } from "./shared";

// Artist Studio > Awards, laid out as in the design: a "Congratulations" banner with the party icon, one tile per award (earned tiles
// have a coloured badge and a View button, locked tiles are grey and say Locked), and a View pop-up with the award picture, the title,
// the track, one sentence about what was reached and a Share button.

const BRAND = "Nyawoo";
// Badge colours for earned awards; locked awards are always grey.
const COLORS = { forest: "#1b3a2d", teal: "#3aa0b5", green: "#43c35f" };
const LOCKED = "#d1cfcc";
const INK = "#1b3a2d";

// The award card is a public picture served by the API (the stored S3 copy is private). Null until the engine has made it.
export const cardHref = (award) =>
  award && award.earned && award.card && award.card.status === "ready" && award.card.image_path ? `${API_BASE_URL}${award.card.image_path}` : null;

// Phones and tablets have a share sheet worth opening; on a computer the system dialog often shows nothing useful, so the link is copied.
const hasShareSheet = () =>
  typeof navigator !== "undefined" && typeof navigator.share === "function" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

export const formatAwardDate = (value) => {
  const d = new Date(value);
  return value && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";
};

const count = (n) => Number(n || 0).toLocaleString("en-US");

// The sentence in the pop-up: what was reached, and by which track when it is a single-track award.
export const awardMessage = (award) => {
  const n = Number(award.threshold) || 0;
  const metric = String(award.metric || "");
  const track = award.track_id && award.subtitle ? `"${award.subtitle}"` : null;
  if (/stream/i.test(metric) && n) return `${track || "Your catalogue"} crossed ${count(n)} streams on ${BRAND}.`;
  if (/licen[sc]e|sale/i.test(metric) && n) {
    return n === 1 ? `You made your first licence sale on ${BRAND}.` : `You have sold ${count(n)} licences on ${BRAND}.`;
  }
  return award.description || `You earned "${award.title}" on ${BRAND}.`;
};

function AwardIcon({ icon, large = false }) {
  if (icon === "dollar") return <span className={large ? "text-5xl font-bold" : "text-2xl font-bold"} aria-hidden="true">$</span>;
  return <span className={large ? "text-4xl" : "text-xl"} aria-hidden="true">★</span>;
}

// What gets shared: a line of text and a public link (the sound for a track award, the site otherwise). The award card
// image itself is offered as a download, because a link to a private storage file would not open for anyone else.
export const shareDetails = (award, origin = window.location.origin) => ({
  title: `${award.title} on ${BRAND}`,
  text: award.subtitle && award.track_id ? `I earned "${award.title}" for ${award.subtitle} on ${BRAND}.` : `I earned "${award.title}" on ${BRAND}.`,
  url: award.track_id ? `${origin}/sound/${award.track_id}` : origin,
});

function ShareButton({ award }) {
  const [note, setNote] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const tell = (text) => {
    setNote(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setNote(""), 3000);
  };
  const share = async () => {
    const details = shareDetails(award);
    try {
      const card = cardHref(award);
      if (hasShareSheet()) {
        await navigator.share(card ? { ...details, text: `${details.text} ${card}` } : details);
        return;
      }
      await navigator.clipboard.writeText(`${details.text} ${details.url}${card ? ` ${card}` : ""}`);
      tell("Link copied");
    } catch (e) {
      if (e && e.name === "AbortError") return; // the person closed the share sheet
      tell("Couldn't share. Copy the link from your browser instead.");
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={share}
        className="rounded-full px-8 py-3 text-lg font-semibold text-white hover:opacity-90"
        style={{ backgroundColor: INK }}
        data-testid={`share-${award.key}`}
      >
        Share
      </button>
      <span className="sr-only" role="status" aria-live="polite">{note}</span>
      {note && <p className="mt-2 text-xs text-gray-600" aria-hidden="true" data-testid={`share-note-${award.key}`}>{note}</p>}
    </>
  );
}

// The badge circle (icon + colour), as in the design.
function AwardBadge({ award, large = false }) {
  const earned = award.earned;
  return (
    <div
      className={`${large ? "h-28 w-28" : "h-16 w-16"} rounded-full flex items-center justify-center text-white`}
      style={{ backgroundColor: earned ? COLORS[award.color] || COLORS.forest : LOCKED }}
    >
      <AwardIcon icon={award.icon} large={large} />
    </div>
  );
}

// The "Congratulations" pop-up opened by View.
export function AwardDialog({ award, onClose }) {
  const cardUrl = cardHref(award);
  const [cardFailed, setCardFailed] = useState(false);
  const closeRef = useRef(null);
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const saveCard = async () => {
    // Saves the picture; if the browser blocks that, it opens in a new tab instead.
    try {
      const response = await fetch(cardUrl);
      if (!response.ok) throw new Error("not ok");
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${award.key}-award-card.svg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      window.open(cardUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="award-dialog-overlay"
    >
      <div className="relative w-full max-w-xl rounded-3xl bg-white px-8 pb-10 pt-8 text-center shadow-xl" role="dialog" aria-modal="true" aria-label={`${award.title} award`} data-testid="award-dialog">
        <button
          type="button"
          ref={closeRef}
          onClick={onClose}
          aria-label="Close"
          className="absolute right-8 top-8 text-gray-500 hover:text-gray-900"
          data-testid="award-dialog-close"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
        </button>
        <p className="mt-2 text-lg font-bold uppercase tracking-wide" style={{ color: COLORS.green }}>
          <span aria-hidden="true">🎉 </span>Congratulations!
        </p>
        <div className="my-8 flex min-h-[150px] items-center justify-center">
          {cardUrl && !cardFailed ? (
            <img src={cardUrl} alt={`${award.title} award card`} className="max-h-56 max-w-full rounded-xl" onError={() => setCardFailed(true)} data-testid="award-dialog-card" />
          ) : (
            <AwardBadge award={award} large />
          )}
        </div>
        <h2 className="text-4xl font-bold" style={{ color: INK }}>{award.title}</h2>
        <p className="mt-4 text-lg font-semibold text-[#095228]">{award.subtitle}</p>
        <p className="mt-6 text-lg text-gray-900" data-testid="award-dialog-message">{awardMessage(award)}</p>
        <div className="mt-8 flex flex-col items-center">
          <ShareButton award={award} />
          {cardUrl && (
            <button type="button" onClick={saveCard} className="mt-3 text-sm font-semibold text-gray-600 underline hover:text-gray-900" data-testid={`download-${award.key}`}>
              Download card
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AwardCard({ award, onView }) {
  const earned = award.earned;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-6 py-7 text-center flex flex-col items-center" data-testid={`award-${award.key}`}>
      <AwardBadge award={award} />
      <h3 className={`mt-4 text-lg font-bold ${earned ? "" : "text-gray-400"}`} style={earned ? { color: INK } : undefined}>{award.title}</h3>
      <p className={`mt-1 text-xs font-semibold ${earned ? "text-[#095228]" : "text-gray-400"}`}>{award.subtitle}</p>
      <p className={`mt-1 text-sm ${earned ? "text-gray-600" : "text-gray-400"}`}>
        {earned ? `Awarded ${formatAwardDate(award.awarded_at)}` : "Locked"}
      </p>
      {earned && (
        <button
          type="button"
          onClick={() => onView(award)}
          className="mt-4 rounded-full border border-gray-300 px-5 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-100"
          data-testid={`view-${award.key}`}
        >
          View
        </button>
      )}
    </div>
  );
}

function AwardsBody() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    trackApi.awards().then(
      (result) => !cancelled && setData(result),
      (e) => {
        if (!cancelled) {
          setError(trackErrorMessage(e, MESSAGES[500]));
          setData({ awards: [], unlocked: 0 });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (data === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loading />
      </div>
    );
  }

  const { awards, unlocked } = data;
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 -mt-2">Milestones earned across your catalogue.</p>
      {error && <p className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">{error}</p>}
      {unlocked > 0 ? (
        <div className="flex items-center gap-6 rounded-2xl px-8 py-7 text-white" style={{ background: "linear-gradient(90deg, #3aa0b5, #43c35f)" }} data-testid="awards-banner">
          <span className="text-5xl leading-none" aria-hidden="true">🎉</span>
          <div>
            <h2 className="text-2xl font-bold">Congratulations on your milestones!</h2>
            <p className="text-base mt-1">
              You've unlocked {unlocked} {unlocked === 1 ? "award" : "awards"} so far — keep releasing and licensing to earn the next one.
            </p>
          </div>
        </div>
      ) : (
        !error && (
          <div className="rounded-2xl border border-gray-200 bg-[#f3f7f4] px-8 py-6">
            <h2 className="text-lg font-bold text-gray-900">No awards yet</h2>
            <p className="text-sm text-gray-600 mt-1">Keep releasing and licensing music to unlock your first award.</p>
          </div>
        )
      )}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {awards.map((award) => <AwardCard key={award.key} award={award} onView={setOpen} />)}
      </div>
      {open && <AwardDialog award={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export default function ArtistAwards() {
  const account = useStudioAccount();
  return (
    <StudioGate
      account={account}
      active="awards"
      heading="Awards"
      tabs={[]}
      maxWidth="max-w-5xl"
    >
      <AwardsBody />
    </StudioGate>
  );
}
